import { ApprovalEvidence } from './ApprovalEvidence';
import { useState } from 'react';
import { Badge, DataTable, type DataTableColumn } from '@zarya/ui';
import { dataHour } from '@app/format';
import { t } from '@app/i18n';
import { useApprovals, type ApprovalsQuery } from './Approvals.api';
import { ChipIdentity, FilterCombo, NoteCount } from './ApprovalsFilters';
import {
  CLOSURES,
  closureLabel,
  reason as reasonBadge,
  reductionLabel,
  reductionTip,
  decisorLabel,
  digestShort,
  identityName,
  reasonDecisionLabel,
  statusLabel,
  statusTone,
  type Approval,
} from './ApprovalsModel';
import s from './Approvals.module.css';

/**
 * Filtro de status do Histórico (FR-068). Cada "auto · {razão}" é um filtro só,
 * ainda que na API vire `status=auto` + `fechamento={razão}`.
 */
/**
 * Função, e não lista de módulo: os rótulos vêm do catálogo e uma lista montada
 * na importação ficaria presa ao idioma daquele instante.
 */
const statusOptions = () => [
  { value: 'aprovada', label: t('approvals.status.aprovada') },
  { value: 'rejeitada', label: t('approvals.status.rejeitada') },
  // Derivado de `CLOSURES`, que é o vocabulário — e não escrito à mão. A lista
  // era fixa em duas das quatro razões do enum `fechamento`, então o Histórico
  // mostrava «auto · ambiente não produtivo» numa linha e não deixava filtrar por
  // ela. Vocabulário em dois lugares diverge; em um, não.
  ...CLOSURES.map((chave) => ({
    value: `auto:${chave}`,
    label: t('approvals.history.filter.status.auto', { closure: closureLabel(chave) }),
  })),
];

const ALL: ApprovalsQuery['status'] = ['aprovada', 'rejeitada', 'auto'];

export interface HistoryTabProps {
  /** Total de decididas segundo o `summary`. */
  total: number;
  onAbrir: (id: string) => void;
  /** `?identidade=` da URL — o mesmo recorte de Pendentes (rodapé de T5). */
  identityId: string | null;
  /** Tira o `?identidade=` e devolve o histórico inteiro. */
  onLimparIdentidade: () => void;
}

/** Aba **Histórico** (FR-068): as decididas, das mais recentes para as mais antigas. */
export function HistoryTab({
  total,
  onAbrir,
  identityId,
  onLimparIdentidade,
}: HistoryTabProps) {
  const [search, setBusca] = useState('');
  const [filter, setFiltro] = useState<string | null>(null);

  const [status, fechamento] = filter?.split(':') ?? [];
  const records = useApprovals({
    status: status ? [status as 'aprovada'] : ALL,
    sort: '-decided_at',
    ...(search.trim() ? { q: search.trim() } : {}),
    ...(fechamento ? { closure: fechamento as NonNullable<ApprovalsQuery['closure']> } : {}),
    ...(identityId ? { identity_id: identityId } : {}),
  });

  const itens = records.data?.items ?? [];

  const columns: DataTableColumn<Approval>[] = [
    {
      key: 'identity',
      header: t('approvals.history.column.identity'),
      cell: (a) => <b>{identityName(a)}</b>,
    },
    {
      key: 'approvalEvidence',
      header: t('approvals.evidence.column'),
      cell: (a) => <ApprovalEvidence approval={a} />,
    },
    {
      key: 'reason',
      header: t('approvals.history.column.reason'),
      cell: (a) => (
        <>
          <Badge tone={reasonBadge(a.reason).tone}>{reasonBadge(a.reason).rotulo}</Badge>
          {/*
            O mesmo selo da aba Pendentes. No Histórico ele não é redundante com
            «auto · redução»: a redução decidida por **uma pessoa** em produção
            fecha como `aprovada`, e sem o selo a linha não diz para que lado a
            autoridade foi (FR-078, nota de 29/08).
          */}
          {a.reduction ? (
            <Badge tone="success" tip={reductionTip()}>
              {reductionLabel()}
            </Badge>
          ) : null}
        </>
      ),
    },
    {
      key: 'release',
      header: t('approvals.history.column.release'),
      mono: true,
      cell: (a) => a.release_tag ?? digestShort(a.release_digest),
    },
    {
      key: 'registrada',
      header: t('approvals.history.column.registered'),
      mono: true,
      cell: (a) => <span className={s.muted}>{dataHour(a.created_at) ?? a.created_at}</span>,
    },
    {
      key: 'status',
      header: t('approvals.history.column.status'),
      cell: (a) => <Badge tone={statusTone(a.status)}>{statusLabel(a)}</Badge>,
    },
    {
      key: 'decidido',
      header: t('approvals.history.column.decidedBy'),
      cell: (a) => (
        <span className={s.nomeTexto}>
          <b>{decisorLabel(a)}</b>
          {a.decided_at ? <span>{dataHour(a.decided_at) ?? a.decided_at}</span> : null}
        </span>
      ),
    },
    {
      key: 'motivoDecisao',
      header: t('approvals.history.column.decisionReason'),
      cell: (a) => {
        const { texto, vazio } = reasonDecisionLabel(a);
        return (
          <span className={[s.motivoDecisao, vazio ? s.muted : ''].filter(Boolean).join(' ')}>
            {texto}
          </span>
        );
      },
    },
  ];

  return (
    <DataTable
      label={t('approvals.history.table.label')}
      columns={columns}
      rows={itens}
      rowKey={(a) => a.id}
      loading={records.isLoading}
      error={records.error}
      onRetry={() => void records.refetch()}
      search={search}
      onSearchChange={setBusca}
      searchPlaceholder={t('approvals.history.searchPlaceholder')}
      filters={
        <>
          {identityId ? (
            <ChipIdentity
              slug={itens[0]?.identity_slug ?? null}
              onRemover={onLimparIdentidade}
            />
          ) : null}
          <FilterCombo
            label={t('approvals.history.filter.status.label')}
            placeholder={t('approvals.history.filter.status.placeholder')}
            options={statusOptions()}
            value={filter}
            onChange={setFiltro}
          />
          <NoteCount exibidas={itens.length} total={total} sufixo={t('approvals.history.note.suffix')} />
        </>
      }
      onClearFilters={() => {
        setBusca('');
        setFiltro(null);
        onLimparIdentidade();
      }}
      filtersApplied={Boolean(search || filter || identityId)}
      emptyIcon="list"
      emptyTitle={t('approvals.history.empty.title')}
      emptyMessage={t('approvals.history.empty.message')}
      onRowClick={(a) => onAbrir(a.id)}
    />
  );
}
